// Minimal allow-list HTML sanitizer for content produced by RichTextEditor.
// Keeps only a small, predefined set of formatting tags/attributes and strips
// everything else (scripts, event handlers, inline styles beyond text-align, etc.).

const ALLOWED_TAGS = new Set([
  'B', 'STRONG', 'I', 'EM', 'U',
  'H3', 'P', 'BR',
  'UL', 'OL', 'LI',
  'DIV', 'SPAN',
]);

// Only text-align is preserved from inline styles (for justify/center support).
const ALLOWED_TEXT_ALIGN = new Set(['left', 'right', 'center', 'justify']);

function cleanNode(node: Node): void {
  const children = Array.from(node.childNodes);
  for (const child of children) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const el = child as HTMLElement;
      if (!ALLOWED_TAGS.has(el.tagName)) {
        // Unwrap disallowed element: keep its (sanitized) children, drop the tag.
        cleanNode(el);
        while (el.firstChild) node.insertBefore(el.firstChild, el);
        node.removeChild(el);
        continue;
      }
      // Strip all attributes except a safe text-align style.
      const align = el.style?.textAlign;
      for (const attr of Array.from(el.attributes)) {
        el.removeAttribute(attr.name);
      }
      if (align && ALLOWED_TEXT_ALIGN.has(align)) {
        el.style.textAlign = align;
      }
      cleanNode(el);
    } else if (child.nodeType !== Node.TEXT_NODE) {
      node.removeChild(child);
    }
  }
}

export function sanitizeHtml(html: string): string {
  if (!html) return '';
  const template = document.createElement('template');
  template.innerHTML = html;
  cleanNode(template.content);
  return template.innerHTML;
}

/** Strips all tags and returns plain text — for tooltips / one-line previews. */
export function htmlToPlainText(html?: string | null): string {
  if (!html) return '';
  const template = document.createElement('template');
  template.innerHTML = html;
  return (template.content.textContent || '').replace(/\s+/g, ' ').trim();
}

/** Convenience prop factory for React's dangerouslySetInnerHTML. */
export function sanitizedHtmlProps(html?: string | null): { dangerouslySetInnerHTML: { __html: string } } {
  return { dangerouslySetInnerHTML: { __html: sanitizeHtml(html || '') } };
}
